export function speechRecognitionCtor() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export function speechSupported() {
  return Boolean(speechRecognitionCtor());
}

export function speechSynthesisSupported() {
  return typeof window !== "undefined" && Boolean(window.speechSynthesis);
}

export function appendTranscript(current, transcript) {
  const next = String(transcript || "").trim();
  if (!next) return String(current || "");
  const cur = String(current || "").trim();
  return cur ? `${cur} ${next}` : next;
}

export function cancelSpeech() {
  if (!speechSynthesisSupported()) return;
  try { window.speechSynthesis.cancel(); } catch { /* engine may already be gone */ }
}

function pickVoice(voices, prefer = []) {
  const list = Array.from(voices || []);
  if (!list.length) return null;
  const wanted = prefer.map((name) => String(name || "").toLowerCase()).filter(Boolean);
  return (
    list.find((voice) => wanted.some((name) => String(voice.name || "").toLowerCase().includes(name)))
    || list.find((voice) => /female|samantha|karen|moira|zira/i.test(voice.name || ""))
    || list.find((voice) => String(voice.lang || "").toLowerCase().startsWith("en"))
    || list[0]
    || null
  );
}

export function speakText(text, options = {}) {
  const phrase = String(text || "").trim();
  if (!phrase || !speechSynthesisSupported()) return false;
  const {
    rate = 1,
    pitch = 1,
    preferVoices = [],
    onstart,
    onend,
    onerror,
  } = options;
  cancelSpeech();
  const utterance = new SpeechSynthesisUtterance(phrase);
  utterance.rate = rate;
  utterance.pitch = pitch;
  const applyVoice = () => {
    const voice = pickVoice(window.speechSynthesis.getVoices(), preferVoices);
    if (voice) utterance.voice = voice;
  };
  applyVoice();
  if (typeof onstart === "function") utterance.onstart = onstart;
  if (typeof onend === "function") utterance.onend = onend;
  if (typeof onerror === "function") utterance.onerror = onerror;
  try {
    window.speechSynthesis.speak(utterance);
    return true;
  } catch {
    onerror?.();
    return false;
  }
}

export function startSpeechRecognition({
  onResult,
  onStart,
  onEnd,
  onError,
  lang = "en-US",
} = {}) {
  const Recognition = speechRecognitionCtor();
  if (!Recognition) return null;
  const recognition = new Recognition();
  recognition.lang = lang;
  recognition.interimResults = false;
  recognition.continuous = false;
  recognition.onstart = () => onStart?.();
  recognition.onend = () => onEnd?.();
  recognition.onerror = (event) => {
    onEnd?.();
    if (event?.error && event.error !== "aborted" && event.error !== "no-speech") {
      onError?.(event);
    }
  };
  recognition.onresult = (event) => {
    const transcript = event.results?.[0]?.[0]?.transcript || "";
    onResult?.(transcript, event);
  };
  try {
    recognition.start();
    return recognition;
  } catch {
    onEnd?.();
    return null;
  }
}
